import { authApi } from 'api';

// A club's teams: school houses, factions, colour teams. The API always says
// "team"; the word a user reads is the club's own (utils/teamLabel.js).
// Every call is refused for a club whose organisation has no teams.

/**
 * Get a club's teams, in list order.
 *
 * @param {string} clubId - The club ID.
 * @param {boolean} [withCounts=false] - true adds each team's number of athletes (athleteCount).
 *   Without it the list is served from the server's cache and costs no database read.
 * @returns {Promise} - The API response; data is [{ teamID, name, colour, sortOrder, athleteCount }].
 */
export const getClubTeams = async (clubId, withCounts = false) => {
  return await authApi.get(
    `/Clubs/${clubId}/teams${withCounts ? '?counts=true' : ''}`
  );
};

/**
 * Add a team.
 *
 * @param {string} clubId - The club ID.
 * @param {Object} team - { name, colour, sortOrder }. colour is '#RRGGBB', or '' for none.
 * @returns {Promise} - The API response.
 */
export const createClubTeam = async (clubId, team) => {
  return await authApi.post(`/Clubs/${clubId}/teams`, team);
};

/**
 * Change a team.
 *
 * @param {string} clubId - The club ID.
 * @param {string} teamId - The team ID.
 * @param {Object} team - { name, colour, sortOrder }. colour '' removes the colour.
 * @returns {Promise} - The API response.
 */
export const updateClubTeam = async (clubId, teamId, team) => {
  return await authApi.put(`/Clubs/${clubId}/teams/${teamId}`, team);
};

/**
 * Delete a team.
 *
 * A team that has athletes is refused with 409 and a report naming how many
 * ({ errorCode, teamId, teamName, athleteCount }). Send the delete again with
 * that number as acknowledgedAthleteCount to confirm: the athletes are left
 * with no team and the team is deleted. If the number has changed in between,
 * the answer is a fresh 409 with the new number.
 *
 * @param {string} clubId - The club ID.
 * @param {string} teamId - The team ID.
 * @param {number} [acknowledgedAthleteCount] - The athlete count from the 409 report being confirmed.
 * @returns {Promise} - The API response.
 */
// The two numbers confirm a delete the server refused (409): the athletes left with no team, and the meets
// whose bonus points are deleted with it (points slice 4c).
export const deleteClubTeam = async (
  clubId,
  teamId,
  acknowledgedAthleteCount,
  acknowledgedBonusPointsMeetCount
) => {
  return await authApi.delete(
    `/Clubs/${clubId}/teams/${teamId}`,
    acknowledgedAthleteCount > 0 || acknowledgedBonusPointsMeetCount > 0
      ? {
          data: {
            resolutions: { acknowledgedAthleteCount, acknowledgedBonusPointsMeetCount },
          },
        }
      : undefined
  );
};
